import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { useToastStore } from '../store/toastStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth tokens
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — auto-refresh on 401
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value: unknown) => void; reject: (reason?: unknown) => void }> = [];

function processQueue(error: unknown, token: string | null = null): void {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
}

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return axiosInstance(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(`${API_URL}/v1/auth/refresh`, {}, { withCredentials: true });
        const newToken = response.data?.data?.access_token;
        if (newToken) {
          localStorage.setItem('auth_token', newToken);
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          processQueue(null, newToken);
          return axiosInstance(originalRequest);
        }
      } catch {
        processQueue(error, null);
        localStorage.removeItem('auth_token');
        window.location.href = '/login';
      } finally {
        isRefreshing = false;
      }
    }

    // Show global toast for non-401 errors
    if (error.response?.status !== 401) {
      const data = error.response?.data as { error?: { message?: string }; message?: string } | undefined;
      const msg = data?.error?.message || data?.message || error.message || 'Error de conexion';
      useToastStore.getState().addToast(msg, 'error');
    }

    return Promise.reject(error);
  }
);

export interface ApiError {
  message: string;
  status: number;
  details?: Record<string, unknown>;
}

function handleError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string; detail?: string }>;
    throw {
      message: axiosError.response?.data?.message || axiosError.response?.data?.detail || axiosError.message,
      status: axiosError.response?.status || 500,
      details: axiosError.response?.data,
    } as ApiError;
  }
  throw { message: 'An unexpected error occurred', status: 500 } as ApiError;
}

export const api = {
  get: async <T>(endpoint: string, params?: Record<string, string | number>, config?: AxiosRequestConfig): Promise<T> => {
    try {
      const response = await axiosInstance.get<T>(endpoint, { params, ...config });
      return response.data;
    } catch (error) {
      handleError(error);
    }
  },

  post: async <T>(endpoint: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> => {
    try {
      const response = await axiosInstance.post<T>(endpoint, data, config);
      return response.data;
    } catch (error) {
      handleError(error);
    }
  },

  put: async <T>(endpoint: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> => {
    try {
      const response = await axiosInstance.put<T>(endpoint, data, config);
      return response.data;
    } catch (error) {
      handleError(error);
    }
  },

  patch: async <T>(endpoint: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> => {
    try {
      const response = await axiosInstance.patch<T>(endpoint, data, config);
      return response.data;
    } catch (error) {
      handleError(error);
    }
  },

  delete: async <T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> => {
    try {
      const response = await axiosInstance.delete<T>(endpoint, config);
      return response.data;
    } catch (error) {
      handleError(error);
    }
  },

  // Upload files with progress
  upload: async <T>(
    endpoint: string,
    formData: FormData,
    onProgress?: (progress: number) => void
  ): Promise<T> => {
    try {
      const response = await axiosInstance.post<T>(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (event) => {
          if (event.total && onProgress) {
            onProgress(Math.round((event.loaded * 100) / event.total));
          }
        },
      });
      return response.data;
    } catch (error) {
      handleError(error);
    }
  },
};

export { axiosInstance };
